'use client'

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { apiClient } from '@/lib/api-client'
import { demoNow } from '@/lib/demo-clock'
import { CoworkerAvatar } from '@/components/coworkers/CoworkerAvatar'
import { StatusIcon, type StatusIconKind } from '@/components/shared/StatusIcon'
import { bellHref, bellIcon, bellTime, type BellIconKind } from '@/components/briefing/briefing-cards'
import { BRIEFING_CHANGED_EVENT } from '@/components/briefing/briefing-copy'

// The bell (plan 02 section 6.13). A briefing entry carries its own link
// (`link_href`), the coworker whose face leads it and the case it belongs to.
// Status icons are the shared solid ones (blue "!" for needs you, green tick
// for completed; no amber). Times read the demo clock: "11:12" on the demo
// day, "Fri 9 Oct" otherwise, never "3h ago".

interface Notification {
  id: number
  type: string
  title: string
  message: string | null
  link_type: string | null // "pr", "po", "cr"
  link_id: string | null   // PR number, PO number, etc.
  link_href?: string | null
  coworker?: string | null
  case_ref?: string | null
  is_read: boolean
  created_at: string | null
}

interface NotificationsResponse {
  unread_count: number
  notifications: Notification[]
}

const ICON_KIND: Record<BellIconKind, StatusIconKind> = {
  needs_you: 'needs_you',
  done: 'done',
  info: 'working',
  error: 'gap',
}

/** Where an entry opens; the router adds the base path to an app link. */
function getNotificationLink(notification: Notification): string | null {
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''
  return bellHref(notification, notification.link_href ? '' : basePath)
}

interface NotificationItemProps {
  notification: Notification
  onMarkAsRead: (id: number) => void
  onNavigate: (url: string) => void
}

function NotificationItem({
  notification,
  onMarkAsRead,
  onNavigate,
}: NotificationItemProps) {
  const link = getNotificationLink(notification)
  const iconKind = ICON_KIND[bellIcon(notification.type)]
  const lines = (notification.message ?? '').split('\n').filter(Boolean)

  const handleClick = () => {
    if (!notification.is_read) {
      onMarkAsRead(notification.id)
    }
    if (link) {
      onNavigate(link)
    }
  }

  return (
    <div
      role={link ? 'link' : undefined}
      tabIndex={link ? 0 : undefined}
      onKeyDown={(event) => {
        if (link && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault()
          handleClick()
        }
      }}
      className={cn(
        'flex gap-3 rounded-xl p-3',
        'transition-colors duration-200 ease-out',
        link ? 'cursor-pointer' : 'cursor-default',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower',
        notification.is_read
          ? 'opacity-60 hover:opacity-100'
          : 'hover:bg-brand-cornflower/5'
      )}
      onClick={handleClick}
    >
      <div className='relative flex-shrink-0'>
        {notification.coworker ? (
          <CoworkerAvatar coworker={notification.coworker} size={32} />
        ) : (
          <StatusIcon kind={iconKind} size={20} />
        )}
        {notification.coworker && (iconKind === 'needs_you' || iconKind === 'done') && (
          <span className='absolute -bottom-1 -right-1'>
            <StatusIcon kind={iconKind} size={14} />
          </span>
        )}
      </div>
      <div className='min-w-0 flex-1'>
        <div className='flex items-start justify-between gap-2'>
          <p className='text-sm font-medium text-foreground'>{notification.title}</p>
          <span className='shrink-0 text-xs text-muted-foreground'>
            {bellTime(notification.created_at, demoNow())}
          </span>
        </div>
        {lines.map((line) => (
          <p key={line} className='mt-0.5 line-clamp-2 text-xs text-muted-foreground'>
            {line}
          </p>
        ))}
        <div className='mt-1 flex items-center justify-between gap-2'>
          {link ? (
            <span className='text-xs font-medium text-brand-cornflower'>Open →</span>
          ) : (
            <span />
          )}
          {!notification.is_read && (
            <span aria-label='Unread' className='h-2 w-2 shrink-0 rounded-full bg-brand-cornflower' />
          )}
        </div>
      </div>
    </div>
  )
}

// Polling interval in milliseconds
const POLL_INTERVAL = 30000 // 30 seconds

export function NotificationCenter() {
  const router = useRouter()
  const [notifications, setNotifications] = React.useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = React.useState(0)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_isLoading, _setIsLoading] = React.useState(false)
  const [isOpen, setIsOpen] = React.useState(false)

  // Fetch notifications from the backend
  const fetchNotifications = React.useCallback(async () => {
    try {
      const data = await apiClient.get<NotificationsResponse>(
        '/api/procurement/notifications?limit=20'
      )
      setNotifications(data.notifications || [])
      setUnreadCount(data.unread_count || 0)
    } catch {
      // Silently fail — user may not be logged in or endpoint may not exist yet
    }
  }, [])

  // Initial fetch + polling
  React.useEffect(() => {
    fetchNotifications()
    const interval = setInterval(fetchNotifications, POLL_INTERVAL)
    // The briefing says when a card changed, so the bell and the cards move together.
    const onChanged = () => void fetchNotifications()
    window.addEventListener(BRIEFING_CHANGED_EVENT, onChanged)
    return () => {
      clearInterval(interval)
      window.removeEventListener(BRIEFING_CHANGED_EVENT, onChanged)
    }
  }, [fetchNotifications])

  // Re-fetch when popover opens
  React.useEffect(() => {
    if (isOpen) {
      fetchNotifications()
    }
  }, [isOpen, fetchNotifications])

  const markAsRead = async (id: number) => {
    try {
      await apiClient.patch(`/api/procurement/notifications/${id}/read`)
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      )
      setUnreadCount((prev) => Math.max(0, prev - 1))
    } catch {
      // Silently fail
    }
  }

  const markAllAsRead = async () => {
    try {
      await apiClient.patch('/api/procurement/notifications/mark-all-read')
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })))
      setUnreadCount(0)
    } catch {
      // Silently fail
    }
  }

  const handleNavigate = (url: string) => {
    setIsOpen(false)
    router.push(url)
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='ghost'
          size='icon'
          className='relative rounded-full text-muted-foreground hover:text-foreground'
        >
          <Icons.bell className='h-5 w-5' strokeWidth={1.5} />
          {unreadCount > 0 && (
            <span
              className={cn(
                'absolute -right-0.5 -top-0.5',
                'flex h-4 min-w-4 items-center justify-center',
                'rounded-full bg-[#1F4FB5] px-1',
                'text-xs font-semibold text-white',
                'animate-badge-bounce'
              )}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align='end' className='w-[22rem] p-0'>
        {/* Header */}
        <div className='flex items-center justify-between border-b border-border/50 px-4 py-3'>
          <h3 className='font-display font-semibold text-foreground'>
            Notifications
          </h3>
          {unreadCount > 0 && (
            <Button
              variant='link'
              size='sm'
              onClick={markAllAsRead}
              className='h-auto p-0 text-xs text-brand-cornflower'
            >
              Mark all read
            </Button>
          )}
        </div>

        {/* Notification list */}
        <div className='max-h-[400px] overflow-y-auto p-2'>
          {notifications.length === 0 ? (
            <div className='py-8 text-center'>
              <div className='mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted/50'>
                <Icons.checkCircle className='h-6 w-6 text-muted-foreground' />
              </div>
              <p className='text-sm font-medium text-foreground'>
                All caught up!
              </p>
              <p className='mt-1 text-xs text-muted-foreground'>
                No new notifications
              </p>
            </div>
          ) : (
            <div className='space-y-1'>
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={markAsRead}
                  onNavigate={handleNavigate}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className='border-t border-border/50 p-2'>
            <Button
              variant='ghost'
              className='w-full text-brand-navy'
              onClick={() => handleNavigate('/procurement/approvals')}
            >
              View all approvals
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
